import { Capacitor } from "@capacitor/core";
import { safeSetItem } from "@/lib/safeStorage";

/**
 * Update Wizard — runs once on every app start.
 *
 * Compares the running APP_VERSION against the version stored in localStorage.
 * When a version bump is detected (overlay APK install without uninstall), it:
 *   1. Migrates legacy "paxel_" localStorage keys to "aphenis_" equivalents
 *   2. Clears stale Vite module caches and service-worker caches
 *   3. Unregisters old service workers so fresh assets are fetched
 *   4. Stores the new version so the migration only fires once
 *
 * This ensures structural UI changes (new settings categories, new routes, etc.)
 * appear immediately after updating the APK, even without a full uninstall.
 *
 * Privacy: No data is sent externally. Only on-device cache keys are affected.
 */

const STORED_VERSION_KEY = "aphenis_installed_version";
const LEGACY_VERSION_KEY = "paxel_installed_version";

/** Current build version — bump this with each release */
export const APP_VERSION = "3.3.7";

/**
 * Migrate all "paxel_*" localStorage keys to "aphenis_*" equivalents.
 * Also handles the legacy "vaultmsg_*" keys which are kept as-is (they
 * predate both brands and are still used for identity/message storage).
 *
 * This runs ONCE — the first time Aphenis launches on a device that
 * previously ran the Paxel-branded build.
 */
function migrateLegacyKeys(): boolean {
  // Check if there are any paxel_ keys to migrate
  const paxelKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("paxel_")) {
      paxelKeys.push(key);
    }
  }

  if (paxelKeys.length === 0) return false;

  console.log(`[UpdateWizard] Found ${paxelKeys.length} legacy paxel_ keys to migrate`);

  for (const oldKey of paxelKeys) {
    const newKey = "aphenis_" + oldKey.slice("paxel_".length);
    const value = localStorage.getItem(oldKey);
    if (value === null) continue;

    // Remove the old key FIRST to free space before writing the new one
    localStorage.removeItem(oldKey);

    // Don't overwrite if aphenis_ version already exists
    if (localStorage.getItem(newKey) === null) {
      try {
        localStorage.setItem(newKey, value);
        console.log(`[UpdateWizard] Migrated ${oldKey} → ${newKey}`);
      } catch (e) {
        console.warn(`[UpdateWizard] Failed to migrate ${oldKey} (quota):`, e);
      }
    }
  }

  return true;
}

/**
 * Returns true if the app was just upgraded (first launch after an overlay install).
 */
export function wasJustUpgraded(): boolean {
  const stored = localStorage.getItem(STORED_VERSION_KEY);
  return stored !== null && stored !== APP_VERSION;
}

/**
 * Returns true if this is a completely fresh install (no stored version).
 */
export function isFreshInstall(): boolean {
  return localStorage.getItem(STORED_VERSION_KEY) === null && localStorage.getItem(LEGACY_VERSION_KEY) === null;
}

/**
 * Run the migration wizard. Should be called once at app startup (main.tsx).
 * Returns { migrated: boolean, previousVersion: string | null }
 */
export async function runUpdateWizard(): Promise<{
  migrated: boolean;
  previousVersion: string | null;
}> {
  // ── 0. Migrate legacy paxel_ keys BEFORE checking version ──
  const hadLegacyKeys = migrateLegacyKeys();

  // If we had a legacy version key, adopt it
  if (localStorage.getItem(STORED_VERSION_KEY) === null) {
    const legacyVersion = localStorage.getItem(LEGACY_VERSION_KEY);
    if (legacyVersion) {
      safeSetItem(STORED_VERSION_KEY, legacyVersion);
      localStorage.removeItem(LEGACY_VERSION_KEY);
      console.log(`[UpdateWizard] Adopted legacy version: ${legacyVersion}`);
    }
  }

  const previousVersion = localStorage.getItem(STORED_VERSION_KEY);

  // Fresh install — just stamp the version
  if (previousVersion === null && !hadLegacyKeys) {
    safeSetItem(STORED_VERSION_KEY, APP_VERSION);
    return { migrated: false, previousVersion: null };
  }

  // Same version — nothing to do
  if (previousVersion === APP_VERSION && !hadLegacyKeys) {
    return { migrated: false, previousVersion };
  }

  console.log(`[UpdateWizard] Migrating from v${previousVersion || "unknown"} → v${APP_VERSION}`);

  // Stamp the new version FIRST — guarantees we never re-enter migration
  // even if the cache/SW work below fails or is slow. Subsequent boots fly past.
  safeSetItem(STORED_VERSION_KEY, APP_VERSION);

  // ── 1. Clear stale module-level localStorage keys (instant, sync) ──
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith("vite-") || key.startsWith("__vite"))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));

  // ── 2. Cache + SW cleanup runs in parallel and is time-boxed ──
  // Capacitor WebView and PWA caches can hang; we never let them block the UI.
  const CACHE_CLEAR_TIMEOUT_MS = 1500;
  const cacheTask = (async () => {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.filter((n) => !n.includes("supabase")).map((n) => caches.delete(n).catch(() => false)),
      );
    } catch (e) {
      console.warn("[UpdateWizard] Cache clear skipped:", e);
    }
  })();

  // For SW we PREFER the standard skip-waiting handshake over destructive
  // unregister — the controllerchange listener in main.tsx triggers a single
  // clean reload when the new SW takes over. Unregister is a fallback.
  const swTask = (async () => {
    try {
      if (!("serviceWorker" in navigator)) return;
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        try {
          await reg.update();
          if (reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        } catch {}
      }
    } catch (e) {
      console.warn("[UpdateWizard] SW update skipped:", e);
    }
  })();

  await Promise.race([
    Promise.all([cacheTask, swTask]),
    new Promise<void>((resolve) => setTimeout(resolve, CACHE_CLEAR_TIMEOUT_MS)),
  ]);

  console.log(`[UpdateWizard] Migration complete → v${APP_VERSION}`);
  return { migrated: true, previousVersion };
}

/**
 * Removes any leftover .apk files from the cache directory.
 * Safe to call on every startup — no-ops silently on web or if no files exist.
 */
export async function cleanupStaleApks(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { files } = await Filesystem.readdir({ path: "", directory: Directory.Cache });

    for (const file of files) {
      if (file.name.endsWith(".apk")) {
        await Filesystem.deleteFile({ path: file.name, directory: Directory.Cache });
        console.log(`[UpdateWizard] Cleaned up stale APK: ${file.name}`);
      }
    }
  } catch (e) {
    // Silently ignore — cache dir may not exist or plugin unavailable
    console.debug("[UpdateWizard] APK cleanup skipped:", e);
  }
}
